import { Badge, Flex, IconName, NavItem, NavList } from '@billy/design-system'
import { YStack } from '@shinetools/lumen-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { useCurrentUser } from '@modules-deprecated/app/user'

import { useIntercom } from '../../../../contexts/intercomContext'
import { EmberRoute } from '../../../../enums/emberRoute'
import { Scope } from '../../../../enums/scope'
import { useEmberRouteUrl } from '../../../../hooks/routing/useEmberRouteUrl'
import { useOpenHelpCenter } from '../../../../hooks/useOpenHelpCenter'
import { HelpModal } from '../../../Modal'
import { NavigationVariant } from '../../types/navigationVariant'
import { toNavItemVariant } from '../../utils/toNavItemVariant'
import { hasRequiredScopes } from '../NavigationMenu/utils/hasRequiredScopes'
import { FEEDBACK_TYPEFORM_URL } from './constants/feedbackTypeformUrl'
import { TrialStatusBanner } from './elements/TrialStatusBanner'
import { NavigationMenuFooterItemId } from './enums/NavigationMenuFooterItemId'
import { useCanUseHelpCenter } from './hooks/useCanUseHelpCenter'

interface NavigationMenuFooterProps {
  variant?: NavigationVariant
}

export const NavigationMenuFooter = ({ variant = 'default' }: NavigationMenuFooterProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useCurrentUser()
  const canUseHelpCenter = useCanUseHelpCenter()
  const { url: integrationsUrl } = useEmberRouteUrl(EmberRoute.Integrations)
  const { unreadConversationsCount: unreadCount } = useIntercom()

  const { isHelpCenterEnabled, openHelp } = useOpenHelpCenter()

  const hasTokensPermission = hasRequiredScopes([Scope.AddOns, Scope.ServicesRead])
  const isIntegrationsPage = location.pathname === integrationsUrl

  const navItemVariant = toNavItemVariant(variant)

  const items: NavItem<NavigationMenuFooterItemId>[] = useMemo(
    () => [
      ...(hasTokensPermission
        ? [
            {
              id: NavigationMenuFooterItemId.AddOns,
              iconLeft: 'puzzle' as IconName,
              value: NavigationMenuFooterItemId.AddOns,
              children: t('navigation.menu.footer.label.add_ons'),
              variant: navItemVariant,
            },
          ]
        : []),
      {
        id: NavigationMenuFooterItemId.Feedback,
        iconLeft: 'messageCircleMore' as IconName,
        value: NavigationMenuFooterItemId.Feedback,
        children: t('navigation.menu.footer.label.feedback'),
        variant: navItemVariant,
      },
      {
        id: NavigationMenuFooterItemId.Help,
        iconLeft: 'circleQuestionMark',
        value: NavigationMenuFooterItemId.Help,
        children: (
          <Flex justifyContent="space-between" width="100%">
            {t('navigation.menu.footer.label.help')}{' '}
            {unreadCount > 0 && (
              <Badge color="danger" mode="subtle">
                {unreadCount}
              </Badge>
            )}
          </Flex>
        ),
        variant: navItemVariant,
      },
    ],
    [t, unreadCount, hasTokensPermission, navItemVariant],
  )

  const handleOnItemClick = useCallback(
    (_: string, value: NavigationMenuFooterItemId) => {
      if (value === NavigationMenuFooterItemId.AddOns && integrationsUrl) {
        navigate(integrationsUrl)
        return
      }

      if (value === NavigationMenuFooterItemId.Help) {
        openHelp()
        return
      }

      if (value === NavigationMenuFooterItemId.Feedback) {
        window.open(`${FEEDBACK_TYPEFORM_URL}&user_id=${user?.id}`, '_blank', 'noopener,noreferrer')
      }
    },
    [openHelp, integrationsUrl, navigate, user?.id],
  )

  if (!canUseHelpCenter) {
    return null
  }

  return (
    <>
      <YStack gap={12}>
        <TrialStatusBanner />
        <NavList items={items} onItemClick={handleOnItemClick} selectedId={isIntegrationsPage ? 'addOns' : undefined} />
      </YStack>
      {!isHelpCenterEnabled && <HelpModal />}
    </>
  )
}
